import { memo, useEffect, useMemo } from "react"
import type { FC } from "react"

import type { TowerPlotDef } from "types/dashboard.types"

import useDateRange from "helpers/hooks/useDateRange"
import { getCellHeight } from "helpers/utils/dashboards"
import type { EntityType } from "types/common.types"
import { useMultiTowerTelemetry } from "helpers/hooks/plots/useMultiTowerTelemetry"
import { usePlotProcessor } from "helpers/hooks/plots/usePlotProcessor"
import { useDashboardContext } from "helpers/hooks/useDashboardContext"

import Plot from "../Plot"

const PlotMemo = memo(Plot)

interface TowerTelemetryPlotProps {
  def: TowerPlotDef
  towerIds: number[]
}

const TowerTelemetryPlot: FC<TowerTelemetryPlotProps> = ({ def, towerIds }) => {
  const { view, reqConfig } = def
  const { config, plotLayout, dispatchPlotLayout, entityType } = useDashboardContext()

  const { getDateRangeTools } = useDateRange()
  const { dateRange, isDateRangeValid } = useMemo(
    () => getDateRangeTools(entityType ?? "tower"),
    [getDateRangeTools, entityType],
  )

  const { data, qualityData, qualityError, isRawData, error, isFetching, refetch } =
    useMultiTowerTelemetry({
      towerIds,
      reqConfig,
      dateRange,
      isDateRangeValid,
    })

  const {
    formattedDef,
    tabLegend,
    fieldQualityMap,
    plotData,
    tooltipData,
    shouldRender,
  } = usePlotProcessor({
    data,
    def,
    entityIds: towerIds,
    isRawData,
    dateRange,
    error,
  })

  const csvData = useMemo(
    (): {
      entity: EntityType
      entityIds: number[]
      fromDate: string
      toDate: string
    } => ({
      entity: "tower",
      entityIds: towerIds,
      fromDate: String(dateRange?.fromDate),
      toDate: String(dateRange?.toDate),
    }),
    [towerIds, dateRange?.fromDate, dateRange?.toDate],
  )

  useEffect(() => {
    if (dispatchPlotLayout && config) {
      dispatchPlotLayout({
        type: "UPDATE_CELL_HEIGHT",
        payload: {
          id: view.nameKey,
          h: shouldRender ? getCellHeight(view.nameKey, config.type) : 0,
        },
      })
    }
  }, [shouldRender, view.nameKey, dispatchPlotLayout, config])

  const render = Boolean(
    shouldRender && (plotLayout === null || plotLayout?.[view.nameKey]?.h !== 0),
  )

  return (
    <>
      {render && (
        <PlotMemo
          data={plotData}
          def={formattedDef}
          csvData={csvData}
          tooltipData={tooltipData}
          entityData={{ entity: "tower", ids: towerIds }}
          qualityData={qualityData}
          qualityError={qualityError}
          isRawData={isRawData}
          seriesLegend={tabLegend}
          fieldQualityMap={fieldQualityMap}
          error={error}
          isFetching={isFetching}
          refetch={refetch}
        />
      )}
    </>
  )
}

export default TowerTelemetryPlot
